import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Business, Service, User } from '../../types';
import { Check, CheckCircle, X, Building2, Calendar, Clock, User as UserIcon, DollarSign, MapPin, Phone, FileText, MessageSquare, Heart, Info } from 'lucide-react';
import { formatDepositDueDurationLabel } from '../../utils/depositDueDuration';

interface ThemeStyles {
  pageBg: string;
  cardBg: string;
  textPrimary: string;
  textSecondary: string;
  border: string;
  buttonPrimary: string;
  buttonSecondary: string;
  buttonSuccess: string;
  progressActive: string;
  progressInactive: string;
  selected: string;
  unselected: string;
  success: string;
  error: string;
  inlineStyles: {
    pageBg: React.CSSProperties;
    cardBg: React.CSSProperties;
    textPrimary: React.CSSProperties;
    textSecondary: React.CSSProperties;
    border: React.CSSProperties;
    buttonPrimary: React.CSSProperties;
    buttonPrimaryHover: React.CSSProperties;
    buttonSecondary: React.CSSProperties;
    buttonSecondaryHover: React.CSSProperties;
    buttonSuccess: React.CSSProperties;
    progressActive: React.CSSProperties;
    progressInactive: React.CSSProperties;
    selected: React.CSSProperties;
    unselected: React.CSSProperties;
    success: React.CSSProperties;
    error: React.CSSProperties;
  };
}

interface BookingData {
  selectedServices: Service[];
  selectedProfessionals: { [serviceId: string]: User | undefined };
  selectedDate: Date | null;
  selectedTime: string;
  totalPrice: number;
  totalDuration: number;
  notes?: string;
  insistOnProfessional?: { [serviceId: string]: boolean };
  recurringSettings?: {
    enabled: boolean;
    mode: 'STRICT' | 'FLEXIBLE';
    intervalWeeks: number;
    occurrenceCount: number;
  };
  recurringPreview?: any | null;
  serviceAddress?: {
    address: string;
    city: string;
    state: string;
    zip: string;
    locationNotes?: string;
    locationLabel?: string;
  };
}

interface BookingStep4Props {
  bookingData: BookingData;
  business: Business;
  onComplete: () => void;
  themeStyles?: ThemeStyles;
  onNotesChange?: (notes: string) => void;
  cancellationPolicy?: {
    cancelRescheduleUpToValue: number;
    cancelRescheduleUpToUnit: 'hours' | 'days';
    cancellationPolicyText: string;
  } | null;
  depositRequired?: boolean;
  depositAmount?: number;
  depositNonRefundableOnCancel?: boolean;
  depositDueWithinValue?: number;
  depositDueWithinUnit?: 'minutes' | 'hours' | 'days';
  hasAcceptedCancellationPolicy?: boolean;
  onAcceptedCancellationPolicyChange?: (next: boolean) => void;
  hasAcceptedDepositLossPolicy?: boolean;
  onAcceptedDepositLossPolicyChange?: (next: boolean) => void;
  hasAcceptedDepositProcessingDeadline?: boolean;
  onAcceptedDepositProcessingDeadlineChange?: (next: boolean) => void;
}

const BookingStep4: React.FC<BookingStep4Props> = ({
  bookingData,
  business,
  onComplete,
  themeStyles,
  onNotesChange,
  cancellationPolicy,
  depositRequired,
  depositAmount,
  depositNonRefundableOnCancel,
  depositDueWithinValue,
  depositDueWithinUnit,
  hasAcceptedCancellationPolicy,
  onAcceptedCancellationPolicyChange,
  hasAcceptedDepositLossPolicy,
  onAcceptedDepositLossPolicyChange,
  hasAcceptedDepositProcessingDeadline,
  onAcceptedDepositProcessingDeadlineChange
}) => {
  // Get primary color from theme or default
  const primaryColor = themeStyles?.inlineStyles?.buttonPrimary.backgroundColor || '#3B82F6';
  const [isPolicyModalOpen, setIsPolicyModalOpen] = useState(false);

  const customPolicyText = (cancellationPolicy?.cancellationPolicyText || '').trim();
  const defaultPolicyTimeLabel = useMemo(() => {
    if (!cancellationPolicy) return '24 hours';
    const value = cancellationPolicy.cancelRescheduleUpToValue ?? 24;
    const unit = cancellationPolicy.cancelRescheduleUpToUnit ?? 'hours';
    const unitLabel = unit === 'days' ? 'days' : 'hours';
    return `${value} ${unitLabel}`;
  }, [cancellationPolicy]);
  const defaultPolicyText = useMemo(
    () =>
      `• No-fee changes: Cancel or reschedule at least ${defaultPolicyTimeLabel} in advance.

• Late cancellations & reschedules: Changes made within ${defaultPolicyTimeLabel} of your appointment may result in a fee. If a deposit was required for this appointment, the deposit is typically forfeited for late cancellation/reschedule. Any refund or credit, if offered, is at the business's discretion.

• No-shows: If you do not arrive for your appointment, you may be charged a fee. If a deposit was required, it is typically forfeited. Any refund or credit, if offered, is at the business's discretion.

• Unexpected events: If severe weather, an emergency, or a business issue prevents service, we'll work with you to reschedule. Any fees may be waived at the business's discretion.`,
    [defaultPolicyTimeLabel]
  );
  const policyText = customPolicyText.length > 0 ? customPolicyText : defaultPolicyText;
  const hasPolicy = policyText.length > 0;
  const hasQuotableService = bookingData.selectedServices.some((s: any) => (s as any)?.quotable === true);
  const depositAmountNum = Number(depositAmount ?? 0);
  const recurringOccurrenceCount =
    bookingData.recurringSettings?.enabled === true
      ? Number(bookingData.recurringSettings.occurrenceCount ?? 1)
      : 1;
  const effectiveOccurrenceCount =
    Number.isFinite(recurringOccurrenceCount) && recurringOccurrenceCount > 0 ? recurringOccurrenceCount : 1;
  const effectiveDepositAmount = depositAmountNum * effectiveOccurrenceCount;
  const isRecurringSeriesDeposit =
    bookingData.recurringSettings?.enabled === true && effectiveOccurrenceCount > 1;
  const showDepositNotice =
    !hasQuotableService &&
    depositRequired === true &&
    Number.isFinite(depositAmountNum) &&
    depositAmountNum > 0;
  const cancelRescheduleSummary = useMemo(() => {
    if (!cancellationPolicy) return '';
    const value = cancellationPolicy.cancelRescheduleUpToValue ?? 24;
    const unit = cancellationPolicy.cancelRescheduleUpToUnit ?? 'hours';
    const unitLabel = unit === 'days' ? 'days' : 'hours';
    return `Cancel or reschedule online up to ${value} ${unitLabel} before your appointment start time.`;
  }, [cancellationPolicy]);

  const hasOnSiteService = bookingData.selectedServices.some((service: any) => {
    const raw = service.serviceLocation || service.service_location;
    const loc = typeof raw === 'string' ? raw.toUpperCase().trim() : '';
    return loc === 'ON_SITE' || loc === 'ONSITE';
  });

  const onSiteAddress = bookingData.serviceAddress;
  const hasValidOnSiteAddress =
    !!onSiteAddress &&
    !!onSiteAddress.address?.trim() &&
    !!onSiteAddress.city?.trim() &&
    !!onSiteAddress.state?.trim() &&
    !!onSiteAddress.zip?.trim();

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (time: string) => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    if (hours > 0) {
      return `${hours}h ${mins > 0 ? `${mins}m` : ''}`;
    }
    return `${mins}m`;
  };

  const getProfessionalDisplayName = (professional: User) => {
    if (professional.firstName && professional.lastName) {
      return `${professional.firstName} ${professional.lastName.charAt(0)}.`;
    }
    return professional.email;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="flex items-center justify-center mb-4">
          <div 
            className="w-12 h-12 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: `${primaryColor}20` }}
          >
            <FileText className="w-6 h-6" style={{ color: primaryColor }} />
          </div>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Review Your Booking</h2>
        <p className="text-gray-600 text-base">
          Please review your appointment details before confirming.
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6 items-start lg:items-stretch">
        {/* Left Column */}
        <div className="w-full lg:w-1/2 flex flex-col gap-6 self-stretch">
          {/* Business Information */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 flex flex-col flex-1">
            <div className="flex items-center gap-3 mb-6">
              <div 
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: `${primaryColor}20` }}
              >
                <Building2 className="w-5 h-5" style={{ color: primaryColor }} />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Business Information</h3>
            </div>
            <div className="space-y-4">
              <div>
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Business Name</span>
                <p className="text-base font-medium text-gray-900 mt-1">{business.name || business.shortName || 'Business Name'}</p>
              </div>
              {business.address && (
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Address</span>
                    <p className="text-base text-gray-700 mt-1">{business.address}</p>
                  </div>
                </div>
              )}
              {business.phone && (
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  <div className="flex-1">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Phone</span>
                    <p className="text-base text-gray-700 mt-1">{business.phone}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Appointment Details */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 flex flex-col">
            <div className="flex items-center gap-3 mb-6">
              <div 
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: `${primaryColor}20` }}
              >
                <Calendar className="w-5 h-5" style={{ color: primaryColor }} />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Appointment Details</h3>
            </div>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-gray-400 flex-shrink-0" />
                <div className="flex-1">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Date</span>
                  <p className="text-base font-medium text-gray-900 mt-1">
                    {bookingData.selectedDate ? formatDate(bookingData.selectedDate) : 'Not selected'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-gray-400 flex-shrink-0" />
                <div className="flex-1">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Time</span>
                  <p className="text-base font-medium text-gray-900 mt-1">
                    {bookingData.selectedTime ? formatTime(bookingData.selectedTime) : 'Not selected'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-gray-400 flex-shrink-0" />
                <div className="flex-1">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Duration</span>
                  <p className="text-base font-medium text-gray-900 mt-1">{formatDuration(bookingData.totalDuration)}</p>
                </div>
              </div>

              {/* Recurring summary (optional) */}
              {bookingData.recurringSettings?.enabled && (
                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Recurring</span>
                    <p className="text-base font-medium text-gray-900 mt-1">
                      {bookingData.recurringSettings.occurrenceCount} appointment(s) • Every {bookingData.recurringSettings.intervalWeeks} week(s)
                    </p>
                    {bookingData.recurringPreview?.occurrences && Array.isArray(bookingData.recurringPreview.occurrences) && (
                      (() => {
                        const occs = bookingData.recurringPreview.occurrences as any[];
                        const conflicts = occs.filter(o => o?.status === 'CONFLICT').length;
                        if (conflicts <= 0) return null;
                        return (
                          <p className="text-sm text-red-700 mt-1">
                            {conflicts} occurrence(s) have a conflict. Please go back and preview again.
                          </p>
                        );
                      })()
                    )}
                  </div>
                </div>
              )}

              {/* Service Location (and on-site address) */}
              {(bookingData.selectedServices.length > 0) && (
                <div className="pt-4 border-t border-gray-200">
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Service Location</span>
                      <div className="mt-1 text-sm font-medium text-gray-900">
                        {(() => {
                          const hasInStore = bookingData.selectedServices.some((s: any) => {
                            const raw = (s as any).serviceLocation || (s as any).service_location;
                            const loc = typeof raw === 'string' ? raw.toUpperCase().trim() : '';
                            return loc === 'IN_STORE' || loc === 'INSTORE';
                          });
                          const hasOnSite = bookingData.selectedServices.some((s: any) => {
                            const raw = (s as any).serviceLocation || (s as any).service_location;
                            const loc = typeof raw === 'string' ? raw.toUpperCase().trim() : '';
                            return loc === 'ON_SITE' || loc === 'ONSITE';
                          });
                          if (hasInStore && hasOnSite) return 'Mixed (In-store + On-site)';
                          if (hasOnSite) return 'On-site';
                          return 'In-store';
                        })()}
                      </div>

                      {hasOnSiteService && hasValidOnSiteAddress && (
                        <div className="mt-3 text-sm text-gray-700">
                          <div className="font-medium">{onSiteAddress!.address}</div>
                          <div className="text-gray-700">
                            {onSiteAddress!.city}, {onSiteAddress!.state} {onSiteAddress!.zip}
                          </div>
                          {onSiteAddress!.locationNotes && onSiteAddress!.locationNotes.trim() !== '' && (
                            <div className="mt-2 text-gray-600">
                              <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Location Notes</span>
                              <div className="mt-1">{onSiteAddress!.locationNotes}</div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="w-full lg:w-1/2 flex flex-col gap-6 self-stretch">
          {/* Selected Services */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 flex flex-col flex-1">
          <div className="flex items-center gap-3 mb-4">
            <div 
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${primaryColor}20` }}
            >
              <CheckCircle className="w-5 h-5" style={{ color: primaryColor }} />
            </div>
            <h3 className="text-xl font-bold text-gray-900">Selected Services</h3>
          </div>
          <div className="flex items-start gap-2 mb-6">
            <Info className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-gray-600">Prices are estimates and may change based on the final services provided.</p>
          </div>
          <div className="space-y-4 flex-1">
            {bookingData.selectedServices.map((service) => {
              const professional = bookingData.selectedProfessionals[service.id];
              const raw = (service as any).serviceLocation || (service as any).service_location;
              const serviceLocation = typeof raw === 'string' ? raw.toUpperCase().trim() : '';
              const isOnSite = serviceLocation === 'ON_SITE' || serviceLocation === 'ONSITE';
              return (
                <div key={service.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1 min-w-0">
                      <h4 className="text-base font-semibold text-gray-900 mb-1">{service.name}</h4>
                      {/* Service Location Badge (replaces description subtext) */}
                      {isOnSite && (
                        <div className="mt-1">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-800">
                            On-Site
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="text-right ml-4 flex-shrink-0">
                      <div className="text-lg font-bold flex items-center justify-end gap-1">
                        <span style={{ color: primaryColor }}>
                          {(service as any)?.quotable === true ? 'Quotable' : formatPrice(Number((service as any)?.price ?? 0))}
                        </span>
                        <span className="text-red-500">*</span>
                      </div>
                      <div className="text-xs text-gray-500 mt-1 flex items-center justify-end gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDuration(service.durationMinutes)}
                      </div>
                    </div>
                  </div>
                  {professional ? (
                    <div className="flex items-center gap-2 pt-3 border-t border-gray-200">
                      <div 
                        className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-semibold"
                        style={{ backgroundColor: primaryColor }}
                      >
                        {getProfessionalDisplayName(professional).split(' ').map(n => n[0]).join('').toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Professional</span>
                        <p className="text-sm font-medium text-gray-900 truncate flex items-center gap-1">
                          {getProfessionalDisplayName(professional)}
                          {bookingData.insistOnProfessional?.[service.id] && (
                            <span className="inline-flex items-center gap-0.5 text-red-500" title="Customer insists on this professional">
                              <Heart className="w-3 h-3 fill-current" />
                              <span className="text-xs">Requested</span>
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 pt-3 border-t border-gray-200">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center bg-gray-300">
                        <UserIcon className="w-4 h-4 text-gray-600" />
                      </div>
                      <div className="flex-1">
                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Professional</span>
                        <p className="text-sm font-medium text-gray-600">Any Professional</p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          </div>

          {/* Special Notes */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 flex flex-col">
          <div className="flex items-center gap-3 mb-4">
            <div 
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${primaryColor}20` }}
            >
              <MessageSquare className="w-5 h-5" style={{ color: primaryColor }} />
            </div>
            <h3 className="text-xl font-bold text-gray-900">Special Notes</h3>
          </div>
          <div className="flex-1 flex flex-col">
            <label htmlFor="special-notes" className="block text-sm font-medium text-gray-700 mb-2">
              Add any special requests or notes for your appointment (optional)
            </label>
            <textarea
              id="special-notes"
              value={bookingData.notes || ''}
              onChange={(e) => onNotesChange?.(e.target.value)}
              placeholder="Enter any special requests or notes for your appointment."
              rows={4}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none resize-none transition-all flex-1"
              onFocus={(e) => {
                e.target.style.borderColor = primaryColor;
                e.target.style.boxShadow = `0 0 0 3px ${primaryColor}20`;
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#d1d5db';
                e.target.style.boxShadow = '';
              }}
            />
            <p className="mt-2 text-xs text-gray-500">
              Let us know if you have any special requirements or preferences for your appointment.
            </p>
          </div>
        </div>
        </div>
      </div>

      {/* Confirmation Message */}
      <div 
        className="rounded-lg p-6 border"
        style={{
          backgroundColor: `${primaryColor}10`,
          borderColor: `${primaryColor}30`
        }}
      >
        <div className="text-center">
          <div className="flex items-center justify-center mb-4">
            <div 
              className="w-12 h-12 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${primaryColor}20` }}
            >
              <CheckCircle className="w-6 h-6" style={{ color: primaryColor }} />
            </div>
          </div>
          <>
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              {hasQuotableService ? 'Ready to Request Quote!' : 'Ready to Book!'}
            </h3>
            <p className="text-base text-gray-700">
              {hasQuotableService
                ? hasPolicy
                  ? 'Your details look good. Please accept the business cancellation policy, then click the button below to submit your quote request.'
                  : 'Your details look good. Click the button below to submit your quote request.'
                : hasPolicy
                  ? 'Your appointment details look good. Please accept the business cancellation policy, then click the button below to confirm your booking.'
                  : 'Your appointment details look good. Click the button below to confirm your booking.'}
            </p>
          </>

          {showDepositNotice && (
            <div
              className="mt-4 mx-auto max-w-lg rounded-lg border bg-white p-4 text-left"
              style={{ borderColor: `${primaryColor}40` }}
            >
              <div className="flex items-start gap-3">
                <div
                  className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${primaryColor}20` }}
                >
                  <DollarSign className="w-5 h-5" style={{ color: primaryColor }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900">Deposit required</p>
                  <p className="mt-1 text-sm text-gray-700">
                    {isRecurringSeriesDeposit ? (
                      <>
                        A deposit of{' '}
                        <span className="font-semibold" style={{ color: primaryColor }}>
                          {formatPrice(depositAmountNum)}
                        </span>{' '}
                        per visit ({effectiveOccurrenceCount} visits) is required —{' '}
                        <span className="font-semibold" style={{ color: primaryColor }}>
                          {formatPrice(effectiveDepositAmount)}
                        </span>{' '}
                        total for the recurring series.
                      </>
                    ) : (
                      <>
                        A deposit of{' '}
                        <span className="font-semibold" style={{ color: primaryColor }}>
                          {formatPrice(depositAmountNum)}
                        </span>{' '}
                        is required to secure your booking.
                      </>
                    )}
                  </p>
                  <p className="mt-2 text-xs text-gray-500">
                    When you click Complete Booking, you&apos;ll be taken to submit payment for{' '}
                    {isRecurringSeriesDeposit ? 'the full series deposit' : 'this deposit'} right away.
                    Your {isRecurringSeriesDeposit ? 'appointments are' : 'appointment is'} not confirmed until the deposit is received.
                  </p>
                </div>
              </div>
            </div>
          )}

          {(hasPolicy || showDepositNotice) && (
            <div className="mx-auto mt-4 flex w-full max-w-lg flex-col items-start gap-3 text-left">
              {hasPolicy && (
                <label className="flex w-full items-start gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={hasAcceptedCancellationPolicy === true}
                    onChange={(e) => onAcceptedCancellationPolicyChange?.(e.target.checked)}
                    className="mt-1 h-4 w-4 shrink-0 border-gray-300 rounded"
                    style={{ accentColor: String(primaryColor) }}
                  />
                  <span>
                    I have read and accept the business&apos;s{' '}
                    <button
                      type="button"
                      onClick={() => setIsPolicyModalOpen(true)}
                      className="underline font-medium"
                      style={{ color: primaryColor }}
                    >
                      cancellation policy
                    </button>
                    .
                  </span>
                </label>
              )}

              {showDepositNotice && (
                <label className="flex w-full items-start gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={hasAcceptedDepositLossPolicy === true}
                    onChange={(e) => onAcceptedDepositLossPolicyChange?.(e.target.checked)}
                    className="mt-1 h-4 w-4 shrink-0 border-gray-300 rounded"
                    style={{ accentColor: String(primaryColor) }}
                  />
                  <span>
                    I understand that I will lose the deposit if I cancel outside of policy
                    {depositNonRefundableOnCancel === true && (
                      <> or may lose the deposit if the store doesn&apos;t allow cancellation after appointment made</>
                    )}
                    .
                  </span>
                </label>
              )}

              {showDepositNotice && (
                <label className="flex w-full items-start gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={hasAcceptedDepositProcessingDeadline === true}
                    onChange={(e) => onAcceptedDepositProcessingDeadlineChange?.(e.target.checked)}
                    className="mt-1 h-4 w-4 shrink-0 border-gray-300 rounded"
                    style={{ accentColor: String(primaryColor) }}
                  />
                  <span>
                    I agree to process the deposit within the next {formatDepositDueDurationLabel(depositDueWithinValue, depositDueWithinUnit)} or the appointment will be cancelled
                    automatically.
                  </span>
                </label>
              )}

              {hasPolicy && cancelRescheduleSummary && (
                <div className="w-full text-xs text-gray-500">{cancelRescheduleSummary}</div>
              )}
            </div>
          )}
        </div>
      </div>

      {isPolicyModalOpen && (
        createPortal(
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4"
            role="dialog"
            aria-modal="true"
          >
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
              <div className="p-5 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Cancellation policy</h3>
                <button
                  type="button"
                  onClick={() => setIsPolicyModalOpen(false)}
                  className="text-gray-500 hover:text-gray-700"
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>
              <div className="p-5 max-h-[70vh] overflow-auto">
                {cancelRescheduleSummary && (
                  <p className="text-sm text-gray-700 font-semibold mb-3">{cancelRescheduleSummary}</p>
                )}
                <div className="text-sm text-gray-800 whitespace-pre-line">{policyText}</div>
              </div>
              <div className="p-5 border-t border-gray-200 flex flex-row justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setIsPolicyModalOpen(false)}
                  className="flex-1 sm:flex-none sm:w-auto px-4 py-2 rounded-lg font-medium border inline-flex items-center justify-center gap-2"
                  style={{ borderColor: String(primaryColor), color: String(primaryColor) }}
                >
                  <X className="w-4 h-4" />
                  Close
                </button>
                {hasAcceptedCancellationPolicy !== true && (
                  <button
                    type="button"
                    onClick={() => {
                      onAcceptedCancellationPolicyChange?.(true);
                      setIsPolicyModalOpen(false);
                    }}
                    className="flex-1 sm:flex-none sm:w-auto px-4 py-2 rounded-lg text-white font-medium inline-flex items-center justify-center gap-2"
                    style={{ backgroundColor: primaryColor }}
                  >
                    <Check className="w-4 h-4" />
                    Accept Policy
                  </button>
                )}
              </div>
            </div>
          </div>,
          document.body
        )
      )}
    </div>
  );
};

export default BookingStep4; 